import React, { useState } from 'react';
import {
  formatPriceWithCurrency,
  getCartItem,
  getDiscountedPrice,
  getIconClassByType,
  getValueOfAttribut,
  shouldShowPromotionUi,
  store,
} from 'helpers';
import Icon from 'components/Icon/Icon';
import { ProductQuantity, ProductTotal } from 'components/Table/Cells/Products';
import ProductFavorite from 'components/ProductFavoriteV3/ProductFavorite';
import PromotionIcon from 'components/Icon/PromotionIcon';
import { productConstants } from 'constants/Product.constants';
import {
  getProductImageFromEan,
  getProductUnit,
  getPcbUnit,
  getProductType,
  formatContratDate,
} from 'helpers/Product';
import { getShouldDisplayOrderedBy } from 'helpers/Platform';
import Tooltip from 'components/Core/Tooltip/Tooltip';
import Moment from 'moment';
import { selectCurrentPlatform } from 'selectors/platform';
import { selectCurrentUser } from 'selectors/user';
import DegressivePrices from 'components/Core/DegressivePrices';
import { platformConstants } from '../../constants';
import DangerousGoodsIcon from './DangerousGoodsIcon';

const agListNameCellRenderer = (product) => {
  if (product.data == null) {
    return null;
  }

  const { client_stock_quantity: clientStockQuantity, name } = product.data;

  const currentPlatform = selectCurrentPlatform(store.getState());
  const { attributs } = currentPlatform;
  const shouldDisplayStock = !Number(
    getValueOfAttribut(attributs || [], platformConstants.STOCK_DISPLAY_OUT_KEY)
  );
  const currentUser = selectCurrentUser(store.getState());
  const shouldDisplayClientStock = currentUser.client.client_stock_display;

  return (
    <div className="ag-list__cell-wrapper">
      <div style={{ fontWeight: '700' }}>{name}</div>
      {!!shouldDisplayStock &&
        availabilityCellRenderer({ value: availabilityValueGetter(product) })}
      {shouldDisplayClientStock && clientStockCellRenderer(clientStockQuantity)}
    </div>
  );
};

const isPlatformLogigua = () => {
  if (parseInt(localStorage.getItem('platform'), 10) === 6) {
    return true;
  }
  return false;
};

const getProductWeightVolume = (productData) => {
  const cartItem = getCartItem(productData.id || productData.product_id);
  const quantity = cartItem ? cartItem.quantity : 0;
  const weight = productData.package && productData.package.weight_gross_package;
  const volume = productData.package && productData.package.volume_package;

  const totalWeight = weight ? parseFloat(weight) * quantity : 0;
  const totalVolume = volume ? parseFloat(volume) * quantity : 0;

  if (totalWeight === 0 && totalVolume === 0) {
    return '-';
  }

  const parts = [];
  if (totalWeight > 0) {
    parts.push(`${totalWeight.toFixed(2)} Kg`);
  }
  if (totalVolume > 0) {
    parts.push(`${totalVolume.toFixed(2)} m3`);
  }

  return parts.length > 0 ? parts.join(' / ') : '-';
};

const agListReferencesCellRenderer = (product) => {
  if (product.data == null) {
    return null;
  }

  const currentPlatform = selectCurrentPlatform(store.getState());
  const { attributs } = currentPlatform;
  const hasPlatformExport =
    Number(getValueOfAttribut(attributs || [], platformConstants.HAS_PLATFORM_EXPORT_KEY)) === 1;

  const {
    ean13,
    reference,
    sale_deadline: saleDeadline,
    class_dangerous_goods: classDangerousGoods,
    package: { contrat_date: contratDate } = {},
  } = product.data;

  const label = hasPlatformExport ? 'Contrat date' : 'DLV';
  const value = hasPlatformExport
    ? contratDate
      ? formatContratDate(contratDate)
      : ' - '
    : !isPlatformLogigua() && saleDeadline
    ? Moment(saleDeadline).format('DD.MM.Y')
    : ' - ';

  return (
    <div className="ag-list__cell-wrapper">
      <div>Réf: {reference}</div>
      <div>EAN: {ean13}</div>
      <div>
        {label}: {value}
      </div>
    </div>
  );
};

const agListWeightVolumeCellRenderer = (product) => {
  if (product.data == null) {
    return null;
  }

  const currentPlatform = selectCurrentPlatform(store.getState());
  const { attributs } = currentPlatform;
  const hasPlatformExport =
    Number(getValueOfAttribut(attributs || [], platformConstants.HAS_PLATFORM_EXPORT_KEY)) === 1;

  if (!hasPlatformExport) {
    return null;
  }

  const cartItem = getCartItem(product.data.id || product.data.product_id);
  const quantity = cartItem ? cartItem.quantity : 0;
  const weight = product.data.package && product.data.package.weight_gross_package;
  const volume = product.data.package && product.data.package.volume_package;

  const totalWeight = weight ? parseFloat(weight) * quantity : 0;
  const totalVolume = volume ? parseFloat(volume) * quantity : 0;

  if (totalWeight === 0 && totalVolume === 0) {
    return (
      <div className="ag-list__cell-wrapper">
        <div>Poids : -</div>
        <div>Volume : -</div>
      </div>
    );
  }

  return (
    <div className="ag-list__cell-wrapper">
      <div>Poids : {totalWeight > 0 ? `${totalWeight.toFixed(2)} Kg` : '-'}</div>
      <div>Volume : {totalVolume > 0 ? `${totalVolume.toFixed(3)} m3` : '-'}</div>
    </div>
  );
};

const agListPricesCellRenderer = (product) => {
  if (product.data == null) {
    return null;
  }

  const {
    degressive_prices: degressivePrices,
    use_degressive_prices: hasDegressivePrices,
    id,
    pcb,
  } = product.data;

  const cartItem = getCartItem(id);
  const { quantity } = cartItem;
  const shouldDisplayOrderedBy = getShouldDisplayOrderedBy();

  return (
    <div
      className={`ag-list__cell-wrapper price-cell ${
        hasDegressivePrices &&
        degressivePrices !== undefined &&
        degressivePrices.length > 0 &&
        quantity > degressivePrices[0].step &&
        'has-discount'
      }`}
    >
      <div className="price-cell__unity">{priceCellRenderer(product, cartItem)}</div>

      {shouldDisplayOrderedBy && shouldDisplayOrderedBy === 1 ? (
        <div className="price-cell__package">{packagePriceCellRenderer(product, cartItem)}</div>
      ) : (
        ''
      )}

      <DegressivePrices product={product.data} />

      {shouldDisplayOrderedBy && shouldDisplayOrderedBy === 1 ? (
        <div>
          PCB: {pcb} {getPcbUnit(product.data)}
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

const favoriteCellRenderer = (product) => {
  if (product.data == null) {
    return null;
  }

  const {
    has_promotion: hasPromotion,
    is_favorite: isFavorite,
    is_new: isNew,
    product_id: productId,
    promotion,
  } = product.data;

  const displayAsPromotion = promotion && promotion.display_as_promotion;

  return (
    <div className="favorite-cell__wrapper">
      <ProductFavorite shouldShowResponsive productId={productId} isFavorite={isFavorite} />
      {hasPromotion && displayAsPromotion && <PromotionIcon />}
      {isNew && <Icon icon="icon-new" slug="is_new" color="red" />}
    </div>
  );
};

const agListImageCellRenderer = (product) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [parentElement, setParentElement] = useState(null);

  if (product.data == null) {
    return null;
  }

  const currentPlatform = selectCurrentPlatform(store.getState());
  const { attributs } = currentPlatform;
  const hasPlatformExport =
    Number(getValueOfAttribut(attributs || [], platformConstants.HAS_PLATFORM_EXPORT_KEY)) === 1;

  const { ean13, type, class_dangerous_goods: classDangerousGoods } = product.data;

  const hasDangerousGoods = classDangerousGoods && classDangerousGoods !== '' && hasPlatformExport;

  return (
    <div className="ag-list__image-cell__wrapper" style={{ position: 'relative' }}>
      {hasDangerousGoods && <DangerousGoodsIcon />}
      {imageCellRenderer({ value: ean13 })}
      <div className="ag-list__image-cell__type-icon-container">
        <Tooltip
          isVisible={isTooltipVisible}
          tooltipPosition="right"
          labelValue={getProductType(type)}
          parentElement={parentElement}
        />
        <i
          className={`${getIconClassByType(type)}`}
          onMouseEnter={(event) => {
            setIsTooltipVisible(true);
            setParentElement(event.target);
          }}
          onMouseLeave={() => {
            setIsTooltipVisible(false);
          }}
        />
      </div>
    </div>
  );
};

const imageCellRenderer = (ean13) => (
  <img
    src={getProductImageFromEan(ean13.value, productConstants.IMAGE_SIZE_SMALL)}
    alt={`${ean13.value}`}
    style={{ width: '35px', height: '35px' }}
    className="img-cell__img"
  />
);

const priceCellRenderer = (product, cartItem) => {
  if (product.data === null) {
    return null;
  }

  const { price, unit } = product.data;
  const discountedPrice = getDiscountedPrice(product.data, cartItem);

  return (
    <>
      {shouldShowPromotionUi(product.data, discountedPrice, price) ? (
        <>
          <span className="is-discounted">
            <span className="price">{formatPriceWithCurrency(price)}</span>
          </span>
          <span className="is-normal">
            {formatPriceWithCurrency(discountedPrice)} / {getProductUnit(unit)}
          </span>
        </>
      ) : (
        <>
          {formatPriceWithCurrency(discountedPrice)} / {getProductUnit(unit)}
        </>
      )}
    </>
  );
};

const agTablePriceCellRenderer = (product) => {
  if (!product.data) {
    return null;
  }

  const { id, price } = product.data;
  const cartItem = getCartItem(id);
  const discountedPrice = getDiscountedPrice(product.data, cartItem);

  return (
    <>
      {shouldShowPromotionUi(product.data, discountedPrice, price) ? (
        <>
          <div className="price-cell">
            <div className="price-cell__unity">
              <span className="is-discounted">
                <span className="price">{formatPriceWithCurrency(price)}</span>
              </span>
              <span className="is-normal">{formatPriceWithCurrency(discountedPrice)}</span>
            </div>
          </div>
        </>
      ) : (
        <>{formatPriceWithCurrency(discountedPrice)}</>
      )}
    </>
  );
};

const packagePriceCellRenderer = (product, cartItem) => {
  if (!product.data) {
    return null;
  }

  const { pcb, price } = product.data;
  const degressivePrice = getDiscountedPrice(product.data, cartItem);

  return (
    <>
      {shouldShowPromotionUi(product.data, degressivePrice, price) ? (
        <>
          <span className="is-discounted">
            <span className="price">{formatPriceWithCurrency(price * pcb)}</span>
          </span>
          <span className="is-normal">
            {formatPriceWithCurrency(degressivePrice * pcb)} / colis
          </span>
        </>
      ) : (
        <>{formatPriceWithCurrency(degressivePrice * pcb)} / colis</>
      )}
    </>
  );
};

const agTablePackagePriceCellRenderer = (product) => {
  if (!product.data) {
    return null;
  }

  const { id, pcb, price } = product.data;
  const cartItem = getCartItem(id);
  const discountedPrice = getDiscountedPrice(product.data, cartItem);

  return (
    <>
      {shouldShowPromotionUi(product.data, discountedPrice, price) ? (
        <>
          <div className="price-cell">
            <div className="price-cell__unity">
              <span className="is-discounted">
                <span className="price">{formatPriceWithCurrency(price * pcb)}</span>
              </span>
              <span className="is-normal">{formatPriceWithCurrency(discountedPrice * pcb)}</span>
            </div>
          </div>
        </>
      ) : (
        <>{formatPriceWithCurrency(discountedPrice * pcb)}</>
      )}
    </>
  );
};

const availabilityCellRenderer = (params) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [parentElement, setParentElement] = useState(null);

  let dotClassName = null;
  let availabilityLabel = null;
  const currentPlatform = selectCurrentPlatform(store.getState());
  const { attributs } = currentPlatform;
  const shouldDisplayStock = !Number(
    getValueOfAttribut(attributs || [], platformConstants.STOCK_DISPLAY_OUT_KEY)
  );

  if (!shouldDisplayStock) {
    return '-';
  }

  switch (params.value) {
    case 'preorder':
      dotClassName = 'orange_background';
      availabilityLabel = 'En Précommande';
      break;
    case 'ok':
      dotClassName = 'green_background';
      availabilityLabel = 'En Stock';
      break;
    default:
      dotClassName = 'red_background';
      availabilityLabel = 'Indisponible';
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <span className={`aggrid__dot ${dotClassName}`} />
      <span
        style={{ position: 'relative' }}
        onMouseEnter={(event) => {
          setIsTooltipVisible(true);
          setParentElement(event.target);
        }}
        onMouseLeave={() => {
          setIsTooltipVisible(false);
        }}
      >
        <Tooltip
          isVisible={isTooltipVisible}
          tooltipPosition="top"
          labelValue="Stock du fournisseur"
        />
        {availabilityLabel}
      </span>
    </div>
  );
};

const clientStockCellRenderer = (clientStock) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [parentElement, setParentElement] = useState(null);

  if (!clientStock && clientStock !== 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span className="aggrid__dot grey_background" />
        <span
          style={{ position: 'relative' }}
          onMouseEnter={(event) => {
            setIsTooltipVisible(true);
            setParentElement(event.target);
          }}
          onMouseLeave={() => {
            setIsTooltipVisible(false);
          }}
        >
          <Tooltip
            isVisible={isTooltipVisible}
            tooltipPosition="top"
            labelValue="Stock du magasin"
            parentElement={parentElement}
          />
          Votre stock: hors assortiment
        </span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <span className={`aggrid__dot ${clientStock > 0 ? 'green_background' : 'red_background'}`} />
      <span
        style={{ position: 'relative' }}
        onMouseEnter={(event) => {
          setIsTooltipVisible(true);
          setParentElement(event.target);
        }}
        onMouseLeave={() => {
          setIsTooltipVisible(false);
        }}
      >
        <Tooltip
          isVisible={isTooltipVisible}
          tooltipPosition="top"
          labelValue="Stock du magasin"
          parentElement={parentElement}
        />
        Votre stock: {clientStock}
      </span>
    </div>
  );
};

const agTableQuantityCellRenderer = (product) => {
  if (product.data == null) {
    return null;
  }

  const { is_favorite: isFavorite, product_id: productId } = product.data;

  return (
    <div className="aggrid__quantity-container">
      <ProductFavorite shouldShowResponsive productId={productId} isFavorite={isFavorite} />
      {quantityCellRenderer(product)}
    </div>
  );
};

const agListQuantityCellRenderer = (product) => {
  if (product.data == null) {
    return null;
  }
  return (
    <div className="ag-list__cell-wrapper ag-list-quantity-wrapper">
      {quantityCellRenderer(product)}
      <div className="ag-list__cell-wrapper__total-cell">{totalCellRenderer(product)}</div>
    </div>
  );
};

const agListTabletRenderer = (product) => {
  if (product.data == null) {
    return null;
  }

  const { name } = product.data;

  return (
    <div className="ag-list__cell-wrapper ag-list-tablet-wrapper">
      <div style={{ fontWeight: '700' }}>{name}</div>
      {agListPricesCellRenderer(product)}
    </div>
  );
};

const agListMobileRenderer = (product) => {
  if (product.data == null) {
    return null;
  }

  const { name } = product.data;

  return (
    <div className="ag-list__cell-wrapper ag-list-mobile-wrapper">
      <div style={{ fontWeight: '700' }}>{name}</div>
      {agListPricesCellRenderer(product)}
      <div className="ag-list-mobile__quantity-wrapper">
        {quantityCellRenderer(product)}
        {totalCellRenderer(product)}
      </div>
    </div>
  );
};

const quantityCellRenderer = (product) => {
  if (product.data == null) {
    return null;
  }
  return <ProductQuantity shouldShowResponsive product={product.data} api={product.api} />;
};

const totalCellRenderer = (product) => {
  if (product.data == null) {
    return null;
  }

  return <ProductTotal shouldShowResponsive product={product.data} float={null} />;
};

const isNewRenderer = (isNew) => (isNew.value === true || isNew.value === 'true' ? 'OUI' : 'NON');

const departmentCellRenderer = (department) => {
  return getProductType(department.value);
};

const availabilityValueGetter = (stock) => {
  if (stock.data == null) {
    return null;
  }

  if (stock.data && stock.data.is_preorder === true) {
    return 'preorder';
  }

  if (stock.data.stock && stock.data.stock.value_cu >= stock.data.pcb) {
    return 'ok';
  }

  return 'ko';
};

const availabilityFilterFormatter = (stock) => {
  if (stock == null) {
    return null;
  }

  if (stock.value === 'preorder') {
    return 'En Précommande';
  }

  if (stock.value === 'ok') {
    return 'En Stock';
  }

  return 'Indisponible';
};

const hasPromotionRenderer = (promotionId) =>
  promotionId.data.promotion_id === null ? 'NON' : 'OUI';

export {
  imageCellRenderer,
  priceCellRenderer,
  packagePriceCellRenderer,
  availabilityCellRenderer,
  quantityCellRenderer,
  totalCellRenderer,
  isNewRenderer,
  departmentCellRenderer,
  availabilityValueGetter,
  availabilityFilterFormatter,
  hasPromotionRenderer,
  agListNameCellRenderer,
  agListReferencesCellRenderer,
  agListWeightVolumeCellRenderer,
  agListPricesCellRenderer,
  agTablePackagePriceCellRenderer,
  agTablePriceCellRenderer,
  agTableQuantityCellRenderer,
  agListQuantityCellRenderer,
  favoriteCellRenderer,
  agListTabletRenderer,
  agListMobileRenderer,
  agListImageCellRenderer,
  clientStockCellRenderer,
};
